import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { LiaPencilAltSolid as PencilIcon, LiaTrashAlt as TrashIcon } from 'react-icons/lia';
import { Logo } from '@/components/Logo';
import {
  db,
  deleteResume,
  getOrCreateResume,
  type ResumeData,
  type ResumeDocument,
  renameResume,
  type Snapshot,
  saveSnapshot,
} from '@/lib/db';
import { generateSnapshot } from '@/lib/snapshot';

export const Route = createFileRoute('/list')({
  component: ListPage,
});

interface ResumeItem {
  resume: ResumeData;
  snapshot: Snapshot | null;
}

function ResumeCard({
  item,
  onDelete,
  onRename,
}: {
  item: ResumeItem;
  onDelete: (id: string) => void;
  onRename: (oldId: string, newId: string) => void;
}) {
  const { resume, snapshot } = item;
  const [isEditing, setIsEditing] = useState(false);
  const [newId, setNewId] = useState(resume.id);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = () => {
    if (confirm(`Delete resume "${resume.id}"? This cannot be undone.`)) {
      onDelete(resume.id);
    }
  };

  const handleRename = async () => {
    const trimmed = newId.trim();
    if (!trimmed) {
      setError('ID cannot be empty.');
      return;
    }
    if (trimmed === resume.id) {
      setIsEditing(false);
      return;
    }
    setError(null);
    try {
      await onRename(resume.id, trimmed);
      setIsEditing(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to rename.');
    }
  };

  const handleCancel = () => {
    setNewId(resume.id);
    setError(null);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleRename();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div key={resume.id} className="group relative flex flex-col w-48">
      <div className="absolute top-1 right-1 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {resume.id !== 'example' && (
          <button
            type="button"
            onClick={handleDelete}
            className="p-1 rounded-md bg-white/80 hover:bg-red-100 hover:text-red-600 transition-colors"
            title="Delete resume"
          >
            <TrashIcon />
          </button>
        )}
        {resume.id !== 'example' && (
          <button
            type="button"
            onClick={() => {
              setNewId(resume.id);
              setError(null);
              setIsEditing(true);
            }}
            className="p-1 rounded-md bg-white/80 hover:bg-blue-100 hover:text-blue-600 transition-colors"
            title="Rename resume"
          >
            <PencilIcon />
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="flex flex-col rounded-lg overflow-hidden border border-blue-400 bg-white">
          <div className="p-2 flex flex-col gap-1">
            <input
              type="text"
              value={newId}
              onChange={(e) => {
                setNewId(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              className="text-sm font-mono border border-gray-300 rounded px-2 py-1 w-full"
              autoFocus
            />
            {error && <span className="text-xs text-red-500">{error}</span>}
            <div className="flex gap-2">
              <button
                onClick={handleRename}
                className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="text-xs bg-gray-300 px-2 py-1 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <Link
            to="/edit/$resumeId"
            params={{ resumeId: resume.id }}
            search={{ editor: undefined }}
            className="flex flex-col rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors"
          >
            <div className="w-full aspect-[1/1.414] bg-gray-200 flex items-center justify-center overflow-hidden">
              {snapshot?.image ? (
                <img
                  src={snapshot.image}
                  alt={`Preview of ${resume.id}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
            <div className="p-2 text-sm font-mono truncate">{resume.id}</div>
          </Link>
        </>
      )}
    </div>
  );
}

function useResumes(refreshKey: number): ResumeItem[] | null {
  const [items, setItems] = useState<ResumeItem[] | null>(null);

  useEffect(() => {
    (async () => {
      // Seed 3 starter resumes (no-op if they already exist).
      const [example, creative, academic] = await Promise.all([
        import('@/components/Resume/sample.json'),
        import('@/components/Resume/creative.json'),
        import('@/components/Resume/academic.json'),
      ]);

      await getOrCreateResume('example', example.default as ResumeDocument);
      await getOrCreateResume('creative', creative.default as ResumeDocument);
      await getOrCreateResume('academic', academic.default as ResumeDocument);

      // Fetch existing resumes and snapshots.
      const resumes = await db.resumes.orderBy('updatedAt').reverse().toArray();
      const snapshots = await db.snapshots.toArray();
      const snapshotMap = new Map<string, Snapshot>();
      for (const snap of snapshots) {
        const existing = snapshotMap.get(snap.resumeId);
        if (!existing || snap.timestamp > existing.timestamp) {
          snapshotMap.set(snap.resumeId, snap);
        }
      }

      // Generate snapshots for any resumes that don't have one yet,
      // or whose snapshot was generated with a different generator.
      for (const resume of resumes) {
        const existingSnapshot = snapshotMap.get(resume.id);
        if (existingSnapshot && existingSnapshot.generator === resume.generator) continue;
        const image = await generateSnapshot(
          resume.content,
          resume.generator,
          resume.generatorOptions
        );
        if (image) {
          await saveSnapshot({
            resumeId: resume.id,
            image,
            timestamp: Date.now(),
            generator: resume.generator,
          });
        }
      }

      // Re-read snapshots after generating any missing ones.
      const updatedSnapshots = await db.snapshots.toArray();
      const finalSnapshotMap = new Map<string, Snapshot>();
      for (const snap of updatedSnapshots) {
        const existing = finalSnapshotMap.get(snap.resumeId);
        if (!existing || snap.timestamp > existing.timestamp) {
          finalSnapshotMap.set(snap.resumeId, snap);
        }
      }
      setItems(
        resumes.map((resume) => ({
          resume,
          snapshot: finalSnapshotMap.get(resume.id) ?? null,
        }))
      );
    })();
  }, [refreshKey]);

  return items;
}

function ListPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const resumes = useResumes(refreshKey);

  const handleDelete = useCallback(async (id: string) => {
    await deleteResume(id);
    setRefreshKey((key) => key + 1);
  }, []);

  const handleRename = useCallback(async (oldId: string, newId: string) => {
    await renameResume(oldId, newId);
    setRefreshKey((key) => key + 1);
  }, []);

  return (
    <div className="flex flex-col p-4 gap-3 mx-auto max-w-4xl w-full">
      <div className="flex flex-row items-center justify-between">
        <Link to="/">
          <Logo className="text-4xl" hoverZoom={true} />
        </Link>
      </div>

      <div className="flex flex-col gap-2">
        <h1 className="text-sm font-bold font-mono uppercase">Resumes saved in this browser:</h1>

        {!resumes ? (
          <div>Loading...</div>
        ) : resumes.length === 0 ? (
          <div className="text-sm text-gray-500">No resumes found.</div>
        ) : (
          <div className="flex flex-row flex-wrap gap-4">
            {resumes.map((item) => (
              <ResumeCard
                key={item.resume.id}
                item={item}
                onDelete={handleDelete}
                onRename={handleRename}
              />
            ))}

            <Link
              to="/new"
              className="flex flex-col w-48 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors"
            >
              <div className="w-full aspect-[1/1.414] flex items-center justify-center">
                <div className="text-6xl text-gray-400">+</div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
